<template>
  <div class="relative">
    <!-- Testimonials Container -->
    <div class="overflow-hidden">
      <div 
        class="flex transition-transform duration-500 ease-in-out"
        :style="{ transform: `translateX(-${currentSlide * 100}%)` }"
      >
        <div 
          v-for="testimonial in testimonials" 
          :key="testimonial.id"
          class="w-full flex-shrink-0 px-4"
        >
          <div class="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
            <!-- Rating Stars -->
            <div class="flex mb-4">
              <svg 
                v-for="i in 5" 
                :key="i"
                class="w-5 h-5 text-yellow-400" 
                fill="currentColor" 
                viewBox="0 0 20 20"
              >
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
              </svg>
            </div>

            <!-- Title -->
            <h3 class="text-xl font-bold text-primary-dark mb-2">
              {{ testimonial.title }}
            </h3>

            <!-- Author -->
            <p class="text-gray-600 mb-4">{{ testimonial.author }}</p>

            <!-- Content -->
            <p class="text-gray-700" v-html="highlightText(testimonial.content)" />
          </div>
        </div>
      </div>
    </div>

    <!-- Navigation Buttons -->
    <button 
      @click="prevSlide"
      class="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-primary text-white rounded-full p-2 shadow-lg hover:bg-primary-dark transition-colors cursor-pointer"
      :class="{ 'opacity-75': currentSlide === 0 }"
    >
      <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
      </svg>
    </button>

    <button 
      @click="nextSlide"
      class="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-primary text-white rounded-full p-2 shadow-lg hover:bg-primary-dark transition-colors cursor-pointer"
      :class="{ 'opacity-75': currentSlide === testimonials.length - 1 }"
    >
      <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
      </svg>
    </button>

    <!-- Dots Navigation -->
    <div class="flex justify-center gap-2 mt-6">
      <button
        v-for="(_, index) in testimonials"
        :key="index"
        @click="goToSlide(index)"
        class="w-2 h-2 rounded-full transition-colors cursor-pointer"
        :class="currentSlide === index ? 'bg-primary scale-125' : 'bg-gray-300 hover:bg-primary/50'"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'

const testimonials = [
  {
    id: 1,
    title: "Mudou completamente minha vida!",
    author: "Maria Silva",
    content: "O mapa numerológico me ajudou a *entender melhor meus pontos fortes e fracos*. Agora sei *exatamente como aproveitar meu potencial* e estou muito mais realizada!"
  },
  {
    id: 2,
    title: "Precisão impressionante!",
    author: "João Santos",
    content: "Fiquei impressionado com a *precisão das análises*. Cada número revelou *aspectos da minha personalidade que eu nem sabia que existiam*. Recomendo muito!"
  },
  {
    id: 3,
    title: "Clareza e direção",
    author: "Ana Oliveira",
    content: "Depois de fazer meu mapa, finalmente *entendi meu propósito de vida*. As informações são *claras e práticas*, me ajudando a *tomar decisões importantes*."
  },
  {
    id: 4,
    title: "Transformador!",
    author: "Carlos Mendes",
    content: "O *autoconhecimento* que obtive através do mapa numerológico foi transformador. Agora *entendo melhor minhas relações* e como posso melhorá-las."
  }
]

const currentSlide = ref(0)
const autoplayInterval = ref<number | null>(null)

const nextSlide = () => {
  currentSlide.value = (currentSlide.value + 1) % testimonials.length
}

const prevSlide = () => {
  currentSlide.value = currentSlide.value === 0 
    ? testimonials.length - 1 
    : currentSlide.value - 1
}

const goToSlide = (index: number) => {
  currentSlide.value = index
  resetAutoplay()
}

const highlightText = (text: string) => {
  return text.replace(/\*(.*?)\*/g, '<span class="text-primary font-medium">$1</span>')
}

const startAutoplay = () => {
  autoplayInterval.value = window.setInterval(() => {
    nextSlide()
  }, 5000) // Change slide every 5 seconds
}

const resetAutoplay = () => {
  if (autoplayInterval.value) {
    clearInterval(autoplayInterval.value)
    startAutoplay()
  }
}

onMounted(() => {
  startAutoplay()
})
</script>

<style scoped>
.overflow-hidden {
  overflow: hidden;
}

/* Smooth scale animation for dots */
.scale-125 {
  transform: scale(1.25);
  transition: transform 0.3s ease;
}

/* Fade animation for navigation buttons */
button {
  transition: all 0.3s ease;
}
</style>